import { CommonModule } from '@angular/common';
import { Component, input, model, OnInit, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { EditorModule } from '@tinymce/tinymce-angular';
import { EventObj } from '@tinymce/tinymce-angular/editor/Events';
import { Editor } from 'tinymce';
import sanitizeHtml from 'sanitize-html';

@Component({
	selector: 'app-email-template-editor',
	templateUrl: './email-template-editor.component.html',
	styles: `
		::ng-deep .tox .tox-edit-area::before {
			border: none !important;
		}
	`,
	standalone: true,
	imports: [CommonModule, EditorModule, FormsModule],
})
export class EmailTemplateEditorComponent implements OnInit {
	disabled = input<boolean>(false);
	tagOptions = input<{ title: string; value: string }[]>();
	onChange = output<string>();

	content = model<string>('');

	tinyMCEInit: any;
	private editor: Editor;

	ngOnInit(): void {
		this.initializeEditor();
	}

	editorReady(editorElement: EventObj<any>): void {
		this.editor = editorElement.editor;
		this.setupAutocompleter(this.editor);
	}

	private setupAutocompleter(editor: Editor): void {
		editor.ui.registry.addAutocompleter('mergeTags', {
			trigger: '{{',
			columns: 1,
			fetch: (pattern, maxResults) => {
				return new Promise((resolve) => {
					const matches =
						this.tagOptions()
							?.map((tag) => ({
								value: `<span class="mceNonEditable merge-tag" data-tag="${tag.value.replace(/"/g, '&quot;')}">${tag.title}</span>`,
								text: `${tag.title}`,
							}))
							.filter(
								(item) =>
									item.text.toLowerCase().indexOf(pattern.toLowerCase()) !== -1,
							)
							.slice(0, maxResults) || [];

					resolve(matches);
				});
			},
			matches: () => true,
			maxResults: 10,
			minChars: 0,
			onAction: (autocompleteApi, rng, value) => {
				editor.selection.setRng(rng);
				editor.insertContent(value);
				autocompleteApi.hide();
			},
		});
	}

	private initializeEditor(): void {
		this.tinyMCEInit = {
			base_url: '/tinymce',
			suffix: '.min',
			height: '100%',
			resize: false,
			branding: false,
			menubar: false,
			statusbar: false,
			license_key: 'gpl',
			indent_use_margin: true,
			line_height_formats: '1 1.1 1.15 1.2 1.3 1.4 1.5 1.6 2 2.5 3.0',
			lists_indent_on_tabs: true,
			noneditable_class: 'mceNonEditable',
			noneditable_regexp: /\{?\{\{[^}]+\}\}\}?/gm, // matches {{handlebar}} and {{{handlebar}}}
			format_noneditable_selector: 'span', // This makes it so that tags can be styled with the toolbar (e.g. bold, italic, etc.) but not edited
			pagebreak_separator: '<span class="pdf-pagebreak-before">&nbsp;</span>',
			pagebreak_split_block: true,
			paste_block_drop: true,
			paste_data_images: true,
			readonly: this.disabled,
			paste_preprocess: (editor, args) => {
				console.log({ args });
				// Targetting content copied from outside the current tab (e.g. from another tab in the same browser window or MS Word)
				if (!args.internal) {
					// Tries to remove all MSO classes from the pasted content
					// Styles in tables are kept to maintain the table structure and cell sizes
					args.content = sanitizeHtml(args.content, {
						allowedAttributes: {
							td: ['colspan', 'rowspan', 'style', 'data-*'],
							th: ['colspan', 'rowspan', 'style', 'data-*'],
							table: ['style', 'class', 'data-*', 'data-*'],
							tr: ['style', 'data-*'],
							p: ['style'],
							span: ['style'],
						},
						allowedClasses: {
							table: [
								'noBorders',
								'table_no_style',
								'table_zebra_striped',
								'table',
								'smart-table',
								'clicked',
								'mceNonEditable',
							],
						},
						allowedStyles: {
							'*': {
								width: [/./g],
								color: [/./g],
								'font-size': [/./g],
								'background-color': [/./g],
								'text-decoration': [/./g],
							},
							table: {
								'border-collapse': [/./g],
								'border-spacing': [/./g],
								width: [/./g],
								border: [/./g],
								'border-color': [/./g],
							},
						},
					});
				}
			},
			plugins: 'lists link image code noneditable autoresize',
			toolbar: [
				{
					items: ['undo', 'redo'],
					name: 'history',
				},
				{
					items: ['bold', 'italic', 'underline', 'strikethrough'],
					name: 'formatting',
				},
				{
					items: ['link', 'image'],
					name: 'insertions',
				},
				{
					items: ['bullist', 'numlist'],
					name: 'lists',
				},
				{
					items: ['code'],
					name: 'code',
				},
			],
			toolbar_mode: 'wrap',
			toolbar_sticky: true,
			content_style: `
				body { 
					margin: 1rem;
					height: 100%;
					min-height: 100%;
				}
				.merge-tag { 
					padding: 0.25rem 0.5rem;  /* px-2 py-1 */
					border-radius: 0.375rem;   /* rounded-md */
					background-color: rgb(220, 252, 231);  /* bg-green-100 */
					color: rgb(22, 101, 52);   /* text-green-800 */
					border: 1px solid rgb(187, 247, 208);  /* border border-green-200 */
					font-family: monospace;
					cursor: default;
				}
			`,
			setup: (editor: Editor) => {
				editor.on('change', () => {
					const content = editor.getContent();
					const processedContent = content.replace(
						/<span[^>]*class="[^"]*merge-tag[^"]*"[^>]*data-tag="([^"]+)"[^>]*>[^<]*<\/span>/g,
						(_, tag) => `{{${this.decodeHtml(tag)}}}`,
					);
					this.onChange.emit(processedContent);
				});

				editor.on('BeforeSetContent', (e) => {
					if (e.content) {
						e.content = e.content.replace(/\{\{([^}]+)\}\}/g, (match, tag) => {
							const encodedTag = tag.replace(/"/g, '&quot;');
							const title = this.findTitleForTag(tag);
							return `<span class="merge-tag" data-tag="${encodedTag}">${title || tag}</span>`;
						});
					}
				});

				editor.on('GetContent', (e) => {
					if (e.content) {
						e.content = e.content.replace(
							/<span[^>]*class="[^"]*merge-tag[^"]*"[^>]*data-tag="([^"]+)"[^>]*>[^<]*<\/span>/g,
							(_, tag) => `{{${this.decodeHtml(tag)}}}`,
						);
					}
				});

				editor.on('click', (e) => {
					const clickedElement = e.target as Element;
					if (clickedElement.classList.contains('merge-tag')) {
						const range = editor.selection.getRng();

						editor.dom.remove(clickedElement);

						editor.selection.setRng(range);
						editor.insertContent('{{');

						const event = new KeyboardEvent('keypress', {
							charCode: '{'.charCodeAt(0),
							bubbles: true,
							cancelable: true,
						});
						editor.fire('keypress', event);
					}
				});
			},
		};
	}

	private decodeHtml(input: string): string {
		return input.replace(/&quot;/g, '"').replace(/&amp;/g, '&');
	}

	private findTitleForTag(tag: string): string | undefined {
		return this.tagOptions()?.find((t) => t.value === tag)?.title;
	}
}
